import type {
  MetricOutput,
  SubvalueByTimestamp as MetricOutputSubvalueByTimestamp,
} from "@/types/api/MetricOutput"

export type SubvalueByTimestamp = MetricOutputSubvalueByTimestamp

export type MetricOutputDisplaySource = Pick<
  MetricOutput,
  "output_type" | "float_value" | "string_value"
> & {
  set_value: Exclude<MetricOutput["set_value"], undefined> | null
  result_json?: Record<string, unknown> | null
  result?: Record<string, unknown> | null
  evidence_references?: MetricOutput["evidence_references"]
  subvalues_by_timestamp?: MetricOutput["subvalues_by_timestamp"] | null
  error_message?: MetricOutput["error_message"]
  runtime_metadata?: MetricOutput["runtime_metadata"] | null
}

export type RunMetricOutput = Omit<
  Pick<
    MetricOutput,
    | "simulation_output_id"
    | "output_type"
    | "float_value"
    | "string_value"
    | "set_value"
    | "result_json"
    | "evidence_references"
    | "subvalues_by_timestamp"
    | "error_status"
    | "error_message"
  >,
  "result_json"
> & {
  result_json: Record<string, unknown>
} & Partial<
    Pick<
      MetricOutput,
      | "metric_id"
      | "organization_id"
      | "created_at"
      | "last_updated_at"
      | "start_time"
      | "end_time"
      | "result"
      | "runtime_metadata"
      | "metric_version_id"
      | "metric_version_number"
    >
  >

/**
 * Coerce a raw `subvalues_by_timestamp` payload to the array the record classes expect.
 * The column is a JSONField, so an endpoint that reads it over raw SQL can hand back a
 * JSON string, and pre-migration rows still hold an object keyed by timestamp. Anything
 * that isn't an array becomes `[]` so callers can index and iterate it safely.
 */
export function toSubvaluesByTimestamp(
  value: unknown,
): SubvalueByTimestamp[] | null {
  if (value == null) {
    return null
  }
  if (Array.isArray(value)) {
    return value as SubvalueByTimestamp[]
  }
  if (typeof value === "string") {
    try {
      const parsed: unknown = JSON.parse(value)
      return Array.isArray(parsed) ? (parsed as SubvalueByTimestamp[]) : []
    } catch {
      return []
    }
  }
  return []
}

export function toMetricOutputDisplaySource(
  metricOutput:
    | MetricOutputDisplaySource
    | MetricOutput
    | RunMetricOutput
    | undefined
    | null,
): MetricOutputDisplaySource | undefined {
  if (!metricOutput) {
    return undefined
  }

  return {
    output_type: metricOutput.output_type,
    float_value: metricOutput.float_value,
    string_value: metricOutput.string_value,
    set_value: metricOutput.set_value ?? null,
    result_json: metricOutput.result_json ?? null,
    result: metricOutput.result ?? null,
    evidence_references: metricOutput.evidence_references,
    subvalues_by_timestamp: metricOutput.subvalues_by_timestamp ?? null,
    error_message: metricOutput.error_message,
    runtime_metadata: metricOutput.runtime_metadata ?? null,
  }
}
